import angular from "angular";

angular
    .module('ui-common')
    .factory('ApiErrorService', ApiErrorService);

function ApiErrorService() {
    const service = {
        getMessage: getMessage,
    };
    return service;

    /**
     * Extracts a human-readable error message from an $http/$resource rejection.
     *
     * Handles every error-body shape the .NET 10 API produces:
     *   - Custom middleware (most thrown business errors):
     *       { "error": "..." }                         (400/404/409/500)
     *   - ASP.NET Core automatic model validation:
     *       { "errors": { "Password": ["..."] }, "title": "...", "status": 400 }
     *   - A bare JSON string body (some shipment endpoints):
     *       "Reference number '...' already in use by another shipment"
     *   - OAuth token endpoint:
     *       { "error": "invalid_grant", "error_description": "..." }
     * It also still understands the legacy .NET Framework { "message": "..." }
     * body and generic ProblemDetails { detail, title }.
     *
     * @param {object} error     The rejection object from an $http/$resource call.
     * @param {string} [fallback] Message to use when nothing usable is found.
     * @returns {string}
     */
    function getMessage(error, fallback) {
        fallback = fallback || '';

        const data = error ? error.data : null;
        if (data === null || data === undefined) {
            return fallback;
        }

        // Some endpoints return a bare string body.
        if (typeof data === 'string') {
            return data.trim() || fallback;
        }

        // Legacy .NET Framework shape: { message: "..." }
        if (typeof data.message === 'string' && data.message.length > 0) {
            return data.message;
        }

        // ASP.NET Core ValidationProblemDetails: { errors: { Field: ["msg", ...] } }
        // Check this before title/detail: title is the generic
        // "One or more validation errors occurred." while errors holds the useful text.
        if (data.errors && typeof data.errors === 'object') {
            const messages = [];
            Object.keys(data.errors).forEach(function (key) {
                const value = data.errors[key];
                if (Array.isArray(value)) {
                    value.forEach(function (m) {
                        if (m) {
                            messages.push(m);
                        }
                    });
                } else if (value) {
                    messages.push(value);
                }
            });
            if (messages.length > 0) {
                return messages.join(' ');
            }
        }

        // OAuth token endpoint: { error: "invalid_grant", error_description: "..." }
        // Prefer the human-readable description over the short error code.
        if (typeof data.error_description === 'string' && data.error_description.length > 0) {
            return data.error_description;
        }

        // Custom middleware (the common case): { error: "..." }
        if (typeof data.error === 'string' && data.error.length > 0) {
            return data.error;
        }

        // ASP.NET Core ProblemDetails: { detail } is more specific than { title }.
        if (typeof data.detail === 'string' && data.detail.length > 0) {
            return data.detail;
        }
        if (typeof data.title === 'string' && data.title.length > 0) {
            return data.title;
        }

        return fallback;
    }
}
